import type { CalendarioRecorte, ProdutoCatalogo, RecortePainel } from '@/types/dashboards';
import { fmtData } from './painelFormat';

/**
 * Filtro dos painéis da Equiplex (período, tipo de mês e produtos) — funções puras.
 *
 * O filtro vive no endereço da página (`?periodo=…&calendario=…&produtos=…`): trocar de
 * aba mantém o filtro, o link pode ser repassado e o PDF o mostra. Os atalhos ("mês
 * atual", "últimos 3 meses"…) vão como nome e o servidor resolve as datas — a conta do
 * mês de produção (16→15) continua num lugar só. Ver
 * `docs/prompts/frente_paineis_filtros_plano_mae.md`.
 */

export const PARAMS_FILTRO = ['periodo', 'inicio', 'fim', 'calendario', 'produtos'] as const;

export const PERIODO_PERSONALIZADO = 'personalizado';

/** Atalhos na ordem da barra. Mesmos nomes de `apps/dashboards/services/recorte.py`. */
export const ATALHOS_PERIODO: { id: string; rotulo: string }[] = [
  { id: 'mes_atual', rotulo: 'Mês atual' },
  { id: 'mes_anterior', rotulo: 'Mês anterior' },
  { id: 'ultimos_3_meses', rotulo: 'Últimos 3 meses' },
  { id: 'ultimos_6_meses', rotulo: 'Últimos 6 meses' },
  { id: 'ultimos_12_meses', rotulo: 'Últimos 12 meses' },
  { id: 'ultimos_7_dias', rotulo: 'Últimos 7 dias' },
  { id: 'ultimos_30_dias', rotulo: 'Últimos 30 dias' },
];

export const CALENDARIOS: { id: CalendarioRecorte; rotulo: string; detalhe: string }[] = [
  { id: 'producao', rotulo: 'Mês de produção', detalhe: 'do dia 16 ao dia 15' },
  { id: 'civil', rotulo: 'Mês comum', detalhe: 'do dia 1 ao último dia' },
];

export interface FiltroPaineis {
  /** `null` = o padrão de cada aba. */
  periodo: string | null;
  inicio: string | null;
  fim: string | null;
  calendario: CalendarioRecorte;
  /** Código de planilha (`1110716`), sem ponto. */
  produtos: string[];
}

export const FILTRO_PADRAO: FiltroPaineis = {
  periodo: null,
  inicio: null,
  fim: null,
  calendario: 'producao',
  produtos: [],
};

const semPonto = (codigo: string) => codigo.replace(/\./g, '').trim();

type Periodo = Pick<FiltroPaineis, 'periodo' | 'inicio' | 'fim'>;

/** Datas valem só juntas (personalizado); "personalizado" sem datas é o padrão. */
function periodoDaUrl(params: URLSearchParams): Periodo {
  const inicio = params.get('inicio');
  const fim = params.get('fim');
  if (inicio && fim) return { periodo: PERIODO_PERSONALIZADO, inicio, fim };
  const periodo = params.get('periodo');
  if (!periodo || periodo === PERIODO_PERSONALIZADO) return { periodo: null, inicio: null, fim: null };
  return { periodo, inicio: null, fim: null };
}

export function filtroDaUrl(params: URLSearchParams): FiltroPaineis {
  const codigos = (params.get('produtos') ?? '').split(',').map(semPonto).filter(Boolean);
  return {
    ...periodoDaUrl(params),
    calendario: params.get('calendario') === 'civil' ? 'civil' : 'producao',
    produtos: [...new Set(codigos)],
  };
}

/** Só o que difere do padrão — é o que vai na URL e na chamada da API. */
export function paramsDoFiltro(filtro: FiltroPaineis): Record<string, string> {
  const params: Record<string, string> = {};
  if (filtro.periodo === PERIODO_PERSONALIZADO && filtro.inicio && filtro.fim) {
    params.inicio = filtro.inicio;
    params.fim = filtro.fim;
  } else if (filtro.periodo && filtro.periodo !== PERIODO_PERSONALIZADO) {
    params.periodo = filtro.periodo;
  }
  if (filtro.calendario !== 'producao') params.calendario = filtro.calendario;
  if (filtro.produtos.length) params.produtos = [...filtro.produtos].sort().join(',');
  return params;
}

export function filtroAtivo(filtro: FiltroPaineis): boolean {
  return Object.keys(paramsDoFiltro(filtro)).length > 0;
}

/** O pedaço do `location.search` que é filtro — vai junto nos links entre painéis. */
export function buscaDoFiltro(search: string): string {
  const atual = new URLSearchParams(search);
  const saida = new URLSearchParams();
  PARAMS_FILTRO.forEach((p) => {
    const valor = atual.get(p);
    if (valor) saida.set(p, valor);
  });
  const texto = saida.toString();
  return texto ? `?${texto}` : '';
}

/** Link para outro painel num período fixo (ex.: o mês da Visão geral), levando tipo de mês e produtos. */
export function buscaComPeriodo(search: string, inicio: string, fim: string): string {
  const atual = new URLSearchParams(search);
  const saida = new URLSearchParams({ inicio, fim });
  ['calendario', 'produtos'].forEach((p) => {
    const valor = atual.get(p);
    if (valor) saida.set(p, valor);
  });
  return `?${saida.toString()}`;
}

// ---------------------------------------------------------------- produtos

/** Minúsculo, sem acento e sem ponto: `"Fisiológico 111.0716"` → `"fisiologico 1110716"`. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\./g, '');
}

/** Cada palavra da busca precisa aparecer no código ou na descrição (com ou sem ponto, sem acento). */
export function produtosQueCasam(catalogo: ProdutoCatalogo[], termo: string): ProdutoCatalogo[] {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return catalogo;
  return catalogo.filter((p) => {
    const alvo = normalizar(`${p.codigo} ${p.codigo_datasul ?? ''} ${p.descricao}`);
    return palavras.every((palavra) => alvo.includes(palavra));
  });
}

/** Grupos por formato, na ordem em que o catálogo já vem (formato e descrição). */
export function porFormato(produtos: ProdutoCatalogo[]): { formato: string; produtos: ProdutoCatalogo[] }[] {
  const grupos = new Map<string, ProdutoCatalogo[]>();
  produtos.forEach((p) => {
    const lista = grupos.get(p.formato) ?? [];
    lista.push(p);
    grupos.set(p.formato, lista);
  });
  return [...grupos.entries()].map(([formato, lista]) => ({ formato, produtos: lista }));
}

/** Marca os que faltam do grupo; se o grupo já está todo marcado, desmarca o grupo. */
export function alternarGrupo(selecionados: string[], grupo: ProdutoCatalogo[]): string[] {
  const codigos = grupo.map((p) => p.codigo);
  const todos = codigos.every((c) => selecionados.includes(c));
  if (todos) return selecionados.filter((c) => !codigos.includes(c));
  return [...new Set([...selecionados, ...codigos])];
}

export function nomeDoProduto(codigo: string, catalogo: ProdutoCatalogo[]): string {
  const produto = catalogo.find((p) => p.codigo === codigo);
  return produto?.descricao ? `${codigo} · ${produto.descricao}` : codigo;
}

// ---------------------------------------------------------------- texto do recorte

/** `"Últimos 3 meses de produção · 16/07/2026 a 15/10/2026"` — a linha que a tela e o PDF mostram. */
export function descreverPeriodo(recorte: RecortePainel): string {
  const tipo = recorte.calendario === 'civil' ? 'mês comum' : 'mês de produção';
  const datas = `${fmtData(recorte.inicio)} a ${fmtData(recorte.fim)}`;
  if (recorte.periodo === PERIODO_PERSONALIZADO || recorte.periodo.endsWith('_dias')) {
    return `${recorte.rotulo} · ${datas} · agrupado por ${tipo}`;
  }
  return `${recorte.rotulo} (${tipo}) · ${datas}`;
}

export function descreverProdutos(recorte: RecortePainel): string {
  const n = recorte.produtos.length;
  if (n === 0) return 'Todos os produtos';
  return n === 1 ? '1 produto' : `${n} produtos`;
}

// ---------------------------------------------------------------- começo de mês

/**
 * Até quantos dias com dado o "mês atual" conta como começo de mês (27/10/2026): o mês de produção
 * vira no dia 16 e as OPs são reportadas com 2 a 3 dias de atraso, então os primeiros dias abrem
 * quase vazios.
 */
export const DIAS_COMECO_DE_MES = 5;

export interface ComecoDeMes {
  /** Primeiro dia do mês atual (`AAAA-MM-DD`). */
  inicio: string;
  /** Dias do mês com dado, até o fim dos dados (0 no dia da virada). */
  dias: number;
  calendario: CalendarioRecorte;
}

/** O período é o mês atual e ele mal começou? `null` quando não é o caso (ou sem `dadosAte`). */
export function comecoDeMes(recorte: RecortePainel | undefined, dadosAte: string | null | undefined): ComecoDeMes | null {
  if (!recorte || recorte.periodo !== 'mes_atual' || !dadosAte) return null;
  const dias = Math.round((isoParaData(dadosAte).getTime() - isoParaData(recorte.inicio).getTime()) / 86_400_000) + 1;
  if (dias > DIAS_COMECO_DE_MES) return null;
  return { inicio: recorte.inicio, dias: Math.max(0, dias), calendario: recorte.calendario };
}

// ---------------------------------------------------------------- datas do calendário

/** `2026-09-16` → `Date` local (meia-noite): o calendário trabalha no fuso do navegador. */
export function isoParaData(iso: string): Date {
  const [ano, mes, dia] = iso.slice(0, 10).split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

export function dataParaIso(data: Date): string {
  const mm = String(data.getMonth() + 1).padStart(2, '0');
  const dd = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mm}-${dd}`;
}

// ---------------------------------------------------------------- filtros locais que sobrevivem ao filtro

/**
 * Formatos marcados num gráfico: a escolha da pessoa (vazia vale), ou o padrão quando nada do
 * que ela marcou existe mais no período (trocar de produto pode tirar o formato da tela).
 */
export function formatosVigentes(escolhidas: string[] | null, disponiveis: string[], padrao: () => string[]): string[] {
  if (escolhidas === null) return padrao();
  if (escolhidas.length === 0) return [];
  const validas = escolhidas.filter((c) => disponiveis.includes(c));
  return validas.length ? validas : padrao();
}

/** O mês escolhido nos chips, se ele ainda está no período; senão o padrão do payload. */
export function mesVigente(escolhido: string | null, meses: { key: string }[], padrao: string): string {
  return escolhido && meses.some((m) => m.key === escolhido) ? escolhido : padrao;
}
